import type { MeshingInput, MeshingResult } from "../render/meshing/types";
import type { MeshWorkerRequest, MeshWorkerResponse } from "./mesh-protocol";

interface QueueEntry {
  readonly id: number;
  readonly input: MeshingInput;
  readonly resolve: (result: MeshingResult) => void;
  readonly reject: (reason: Error) => void;
}

interface PoolWorker {
  worker: Worker;
  active: QueueEntry | null;
}

export type WorkerFactory = () => Worker;

/** Fixed-size worker pool; every large request and response buffer is transferred. */
export class MeshWorkerPool {
  private readonly workers: PoolWorker[];
  private readonly queue: QueueEntry[] = [];
  private nextId = 1;
  private stopped = false;

  constructor(
    size = Math.max(1, Math.min(4, (globalThis.navigator?.hardwareConcurrency ?? 4) - 1)),
    private readonly factory: WorkerFactory = () => new Worker(new URL("./mesh.worker.ts", import.meta.url), { type: "module" }),
  ) {
    if (!Number.isInteger(size) || size < 1) throw new RangeError("worker pool size must be a positive integer");
    this.workers = Array.from({ length: size }, () => this.createWorker());
  }

  get pending(): number {
    return this.queue.length + this.workers.filter((slot) => slot.active !== null).length;
  }

  mesh(input: MeshingInput): Promise<MeshingResult> {
    if (this.stopped) return Promise.reject(new Error("mesh worker pool is stopped"));
    return new Promise((resolve, reject) => {
      this.queue.push({ id: this.nextId++, input, resolve, reject });
      this.dispatch();
    });
  }

  terminate(): void {
    this.stopped = true;
    const error = new Error("mesh worker pool terminated");
    for (const entry of this.queue.splice(0)) entry.reject(error);
    for (const slot of this.workers) {
      slot.active?.reject(error);
      slot.active = null;
      slot.worker.terminate();
    }
  }

  private createWorker(): PoolWorker {
    const worker = this.factory();
    const slot: PoolWorker = { worker, active: null };
    this.listen(slot, worker);
    return slot;
  }

  private listen(slot: PoolWorker, worker: Worker): void {
    worker.onmessage = (event: MessageEvent<MeshWorkerResponse>) => {
      if (slot.worker !== worker) return;
      const entry = slot.active;
      if (!entry || event.data.id !== entry.id) return;
      slot.active = null;
      if ("error" in event.data) entry.reject(new Error(event.data.error));
      else entry.resolve(event.data.result);
      this.dispatch();
    };
    worker.onerror = (event) => {
      if (slot.worker !== worker) return;
      event.preventDefault?.();
      this.replaceFailedWorker(slot, new Error(event.message || "meshing worker failed"));
      this.dispatch();
    };
  }

  private replaceFailedWorker(slot: PoolWorker, error: Error): void {
    const failed = slot.worker;
    const entry = slot.active;
    slot.active = null;
    failed.onmessage = null;
    failed.onerror = null;
    failed.terminate();
    entry?.reject(error);
    if (this.stopped) return;
    const replacement = this.factory();
    slot.worker = replacement;
    this.listen(slot, replacement);
  }

  private dispatch(): void {
    if (this.stopped) return;
    for (const slot of this.workers) {
      if (slot.active !== null) continue;
      const entry = this.queue.shift();
      if (!entry) return;
      slot.active = entry;
      const request: MeshWorkerRequest = { id: entry.id, input: entry.input };
      const transfers: Transferable[] = [
        entry.input.blocks.buffer,
        entry.input.light.buffer,
        entry.input.tables.opaque.buffer,
        entry.input.tables.group.buffer,
        entry.input.tables.color.buffer,
      ];
      try {
        slot.worker.postMessage(request, transfers);
      } catch (error) {
        this.replaceFailedWorker(slot, error instanceof Error ? error : new Error(String(error)));
        this.dispatch();
      }
    }
  }
}
